/**
 * Command palette stile Superhuman/Linear.
 * Apri con Cmd+K (gia` collegato in useKeyboardShortcuts).
 */
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
  Inbox, Send, FileText, Trash2, Settings, Pencil, Archive, Sparkles,
  Mail, LogOut, Sun, Moon, Monitor, AlertOctagon, Star, Bell,
} from 'lucide-react';
import { usePalette } from '@/stores/palette.store';
import { useComposer } from '@/stores/composer.store';
import { useInbox } from '@/stores/inbox.store';
import { useTheme } from '@/stores/theme.store';
import { useAuth } from '@/stores/auth.store';
import type { FolderRole } from '@/lib/api/endpoints';

export function CommandPalette() {
  const navigate = useNavigate();
  const open = usePalette((s) => s.open);
  const close = usePalette((s) => s.close);
  const openCompose = useComposer((s) => s.openCompose);
  const setFolder = useInbox((s) => s.setFolder);
  const setCategory = useInbox((s) => s.setCategory);
  const setUrgency = useInbox((s) => s.setUrgency);
  const setTheme = useTheme((s) => s.setTheme);
  const logout = useAuth((s) => s.logout);

  function run(action: () => void) {
    action();
    close();
  }

  function go(folder: FolderRole) {
    setFolder(folder);
    navigate('/');
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(o) => (o ? null : close())}
      label="Command palette"
      className="fixed left-1/2 top-[20%] z-50 -translate-x-1/2"
    >
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={close}
        aria-hidden
      />
      <div className="relative w-[640px] max-w-[92vw] rounded-xl border bg-popover text-popover-foreground shadow-2xl overflow-hidden">
        <Command shouldFilter>
          <Command.Input
            autoFocus
            placeholder="Cerca un'azione..."
            className="w-full h-12 px-4 text-sm bg-transparent border-b focus:outline-none placeholder:text-muted-foreground"
          />
          <Command.List className="max-h-[400px] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              Nessun risultato.
            </Command.Empty>

            <Command.Group heading="Azioni">
              <PaletteItem icon={<Pencil />} label="Scrivi nuovo messaggio" shortcut="C"
                onSelect={() => run(() => openCompose())} />
              <PaletteItem icon={<Settings />} label="Apri impostazioni"
                onSelect={() => run(() => navigate('/settings'))} />
              <PaletteItem icon={<LogOut />} label="Esci"
                onSelect={() => run(() => { void logout(); navigate('/login'); })} />
            </Command.Group>

            <Command.Group heading="Naviga">
              <PaletteItem icon={<Inbox />} label="Vai a Inbox" shortcut="G I"
                onSelect={() => run(() => go('inbox'))} />
              <PaletteItem icon={<Send />} label="Vai a Inviati" shortcut="G S"
                onSelect={() => run(() => go('sent'))} />
              <PaletteItem icon={<FileText />} label="Vai a Bozze" shortcut="G D"
                onSelect={() => run(() => navigate('/drafts'))} />
              <PaletteItem icon={<Archive />} label="Vai ad Archivio"
                onSelect={() => run(() => go('archive'))} />
              <PaletteItem icon={<AlertOctagon />} label="Vai a Spam"
                onSelect={() => run(() => go('spam'))} />
              <PaletteItem icon={<Trash2 />} label="Vai al Cestino" shortcut="G T"
                onSelect={() => run(() => go('trash'))} />
            </Command.Group>

            <Command.Group heading="Filtri AI">
              <PaletteItem icon={<Mail />} label="Filtra: Preventivi"
                onSelect={() => run(() => { setCategory('preventivo'); navigate('/'); })} />
              <PaletteItem icon={<Mail />} label="Filtra: Fatture"
                onSelect={() => run(() => { setCategory('fattura'); navigate('/'); })} />
              <PaletteItem icon={<Mail />} label="Filtra: Supporto"
                onSelect={() => run(() => { setCategory('supporto'); navigate('/'); })} />
              <PaletteItem icon={<Bell />} label="Filtra: FYI"
                onSelect={() => run(() => { setCategory('fyi'); navigate('/'); })} />
              <PaletteItem icon={<Sparkles />} label="Solo urgenti (high/critical)"
                onSelect={() => run(() => { setUrgency('high'); navigate('/'); })} />
              <PaletteItem icon={<Star />} label="Mostra tutto, rimuovi filtri"
                onSelect={() => run(() => { setCategory(null); setUrgency(null); navigate('/'); })} />
            </Command.Group>

            <Command.Group heading="Tema">
              <PaletteItem icon={<Sun />} label="Tema: Chiaro"
                onSelect={() => run(() => setTheme('light'))} />
              <PaletteItem icon={<Moon />} label="Tema: Scuro"
                onSelect={() => run(() => setTheme('dark'))} />
              <PaletteItem icon={<Monitor />} label="Tema: Sistema"
                onSelect={() => run(() => setTheme('system'))} />
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </Command.Dialog>
  );
}

interface ItemProps {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  onSelect: () => void;
}

function PaletteItem({ icon, label, shortcut, onSelect }: ItemProps) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center gap-3 px-2 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      <span className="h-4 w-4 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      <span className="flex-1">{label}</span>
      {shortcut && (
        <kbd className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {shortcut}
        </kbd>
      )}
    </Command.Item>
  );
}
